import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      // M5.9: disable refetch-on-window-focus globally.
      // The default `true` refetches ALL stale queries
      // every time the window regains focus (which
      // includes clicking into a form input — the
      // 'focus' event bubbles to the window). With the
      // admin Users page in the background, focusing
      // the Coins or Reason input triggered parallel
      // refetches of /api/users, /api/me, /api/me/teams,
      // /api/me/coins, /api/competencies, etc. Each
      // request is a network round-trip; the browser
      // ends up with a queue of in-flight requests,
      // and the user's keystroke is delayed by the
      // 3-5 seconds the queue takes to drain. The
      // observed symptom: 'click into the Coins text
      // box, 3 sec pause before my keystroke is shown.'
      //
      // The default behaviour was intended to keep
      // background data fresh when the user comes back
      // to the tab after a long absence. In practice:
      //   - staleTime: 30_000 already gates the
      //     refetches (only stale queries refetch)
      //   - The admin dashboard's CoinBalanceChip
      //     explicitly opts in via
      //     refetchOnWindowFocus: true + refetchInterval
      //     for the live-updating coin count
      //   - The TeamRewardCard does the same
      //   - Everything else can wait for the user to
      //     navigate to a different page or for the
      //     30s staleTime to expire
      //
      // Disabling the global default doesn't break
      // the explicit opt-ins (they override the
      // default). It just stops the silent background
      // refetch storm on every focus event.
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
